import React, { useEffect, useContext, useState } from "react"
import CloseModal from "../CloseModal"
import ContactAvatar from "../contacts/ContactAvatar"
import moment from "moment"
import useLoading from "../../hooks/useLoading"
import { Button } from "../ui/button"
import { ServiceRequests, MeetingRequests } from "../../api/app.service"
import {
  SvgUser3,
  SvgCalendar,
  SvgWebsite,
  SvgVideoOutline,
  SvgCheckCircleSolid,
  SvgFilledInformationCircle,
  SvgUsersSolid,
  SvgWarning,
} from "../icons"
import { useLocation } from "react-router-dom"
import { useNavigate } from "react-router-dom"
import { UserCtx } from "../../context/userContext"
import { useToast } from "../../hooks/useToast"

interface Props {
  checkoutSuccessPopup: any
  contact: any
  meetingDetails?: {
    startTime: Date
    endTime: Date
  }
  activeContact: any
  activeService: any
  paymentSource?: "paypal" | "card" | "apple_pay" | "google-pay" | null
  isFreeService?: boolean
}

const CheckoutSuccessPopup: React.FC<Props> = ({
  checkoutSuccessPopup,
  contact,
  meetingDetails,
  activeContact,
  activeService,
  paymentSource,
  isFreeService,
}) => {
  const { user } = useContext(UserCtx)
  const { toast } = useToast()
  const { startLoading } = useLoading()

  const [service, setService] = useState<any>(null)
  const freeService = Boolean(
    activeService?.service_details?.billing.amount === 0 || isFreeService
  )
  const navigate = useNavigate()
  const location = useLocation()
  const getUserTimezone = () => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const longTimezoneName = new Date().toLocaleTimeString("en", {
      timeZoneName: "long",
    })
    const extractedTimezone = longTimezoneName.split(" ")[2]
    const timezoneWithTime = `${extractedTimezone} Time`
    let timezoneDisplay = timezoneWithTime.replace("_", " ")
    timezoneDisplay = timezoneDisplay.replace("/", " & ")
    timezoneDisplay = `${timezoneDisplay} - ${timezone}`
    return timezoneDisplay
  }
  const userTimezone = getUserTimezone()
  const pathParts = location.pathname.split("/").filter(Boolean)
  const guestCheckout = pathParts.length === 3 && pathParts[1] === "checkout"
  const guestEnrollment = pathParts.length === 2 && !isNaN(Number(pathParts[1]))

  const getPaymentSourceLabel = () => {
    switch (paymentSource) {
      case "apple_pay":
        return "Apple Pay"
      case "google-pay":
        return "Google Pay"
      case "card":
        return "card"
      case "paypal":
        return "PayPal"
      default:
        return null
    }
  }

  const getProfileService = () => {
    let coach_id = 0
    let client_id = 0

    if (!user.isCoach) {
      coach_id = activeContact.profile_id
      client_id = user.activeProfileId
    } else {
      if (activeContact.profile_type === "member") {
        coach_id = user.activeProfileId
        client_id = activeContact.profile_id
      } else {
        coach_id = activeContact.profile_id
        client_id = user.activeProfileId
      }
    }

    if (user) {
      ServiceRequests.getServiceListProfile({
        coach_profile_id: coach_id,
        member_profile_id: client_id,
      })
        .then((data) => {
          const serviceIdToMatch = parseInt(location.pathname.split("/")[4])
          const selectedService = data.service_list.find(
            (service: any) => service.service_id === serviceIdToMatch
          )
          setService(selectedService)
        })
        .catch((ex) => {
          console.error(ex)
          toast.error(ex.response.data.message)
        })
    }
  }

  useEffect(() => {
    getProfileService()
  }, [])

  const handleScheduleSessionClick = () => {
    navigate(`/${user.activeProfile}/scheduling`, {
      state: { service, contact, flag: false },
    })
  }

  const handleStartMessage = () => {
    startLoading()
    window.location.href = `/${user.activeProfile}/inbox?chat=${activeContact.profile_id}`
  }

  const serviceDetails = activeService?.service_details
  const groupDuration = serviceDetails?.session?.duration
  const isGroupService = activeService?.service_details?.isGroupService
  const isRecurringService = serviceDetails?.type === "recurring"
  const repeatBy = serviceDetails?.group?.repeatBy
  const weeklyDayTimes = serviceDetails?.group?.weeklyDayTimes
  const [firstMeetingDate, setFirstMeetingDate] = useState<Date | null>(null)

  useEffect(() => {
    if (isRecurringService && activeService?.stripe_product_id) {
      MeetingRequests.getRecurringMeetings({
        user_id: activeContact?.user_id,
        stripe_connect_product_id: activeService?.stripe_product_id,
      })
        .then((response: { meetings: { start_time: string }[] }) => {
          const meetings = response.meetings || []
          if (meetings.length > 0) {
            const sortedMeetings = meetings.sort(
              (a, b) =>
                new Date(a.start_time).getTime() -
                new Date(b.start_time).getTime()
            )
            setFirstMeetingDate(new Date(sortedMeetings[0].start_time))
          }
        })
        .catch((error) => {
          console.error("Failed to fetch recurring meetings:", error)
        })
    }
  }, [
    isRecurringService,
    activeService?.stripe_product_id,
    activeContact?.user_id,
  ])

  const formatRecurringSchedule = () => {
    if (!weeklyDayTimes || weeklyDayTimes.length === 0) return null
    const firstTime = weeklyDayTimes[0]
    return moment(firstTime.time, "HH:mm").format("h:mm A")
  }

  const firstValidGroup = serviceDetails?.group?.meetingDates?.find(
    (date: string) => {
      const parsedDate = new Date(date)
      return !isNaN(parsedDate.getTime())
    }
  )

  const groupEnd =
    firstValidGroup && groupDuration
      ? new Date(
          new Date(firstValidGroup).getTime() + (groupDuration / 60) * 60000
        )
      : null

  const coachWebsite = () =>
    navigate(`/providers/${activeContact.endpoint_slug}`)

  return (
    <div className="zoee-backdrop-filter credentials fixed left-0 top-0 z-[1000] flex h-screen w-screen items-center justify-center">
      <div className="main-shadow fixed left-1/2 flex h-auto max-h-[85vh] w-[90%] sm:w-[70%] md:w-[55%] lg:w-[45%] -translate-x-1/2 flex-col items-center gap-4 overflow-y-auto rounded-xl bg-white px-4 py-10 md:px-10">
        {guestCheckout || guestEnrollment ? (
          <>
            <div className="p-2">
              <div className="text-4xl font-bold text-center mb-6">
                You're All Set!
              </div>
              <div className="mb-6">
                Your {guestCheckout ? "payment" : "enrollment"} was succesful.
                You’re now connected with{" "}
                <span className="text-blurple font-bold">
                  Coach {activeContact.contact_name}
                </span>{" "}
                for{" "}
                <span className="font-bold">
                  {activeService.service_details.title}
                </span>
                .
              </div>
              <div className="mb-6">
                <div>
                  We’ve sent a confirmation email with your enrollment details
                  and next steps.
                </div>
                <div>
                  {"\u{1F449}"} Be sure to check your spam or junk folder - just
                  in case!
                </div>
              </div>
              <div>
                <Button variant="rounded" onClick={coachWebsite}>
                  Visit Coach {activeContact.contact_name}'s Website
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            <CloseModal
              callback={() => {
                localStorage.removeItem("coachRedirect")
                localStorage.removeItem("memberRedirect")

                if (user.activeProfile === "member") {
                  navigate(`/${user.activeProfile}`)
                } else {
                  navigate(`/${user.activeProfile}/dashboard`)
                }
              }}
              styling="absolute top-6 right-8"
            />
            <ContactAvatar
              contact={{
                contact_name: activeContact.contact_name,
                has_avatar: activeContact.avatar_url,
                user_color: activeContact.user_color,
                user_id: activeContact.user_id,
              }}
              width={80}
              height={80}
              border="rounded-full"
            />
            {meetingDetails || isGroupService ? (
              <div>
                <p className="flex items-center justify-center text-center text-2xl font-bold">
                  <SvgCheckCircleSolid className="mr-2" />{" "}
                  <span>You are scheduled!</span>
                </p>
                <div className="text-center">
                  <p className="text-muted-foreground mt-2 mb-2 text-[#45474a]">
                    A calendar invitation has been sent to your email address.
                  </p>
                </div>
                <div className="border-2 border-gray p-4 rounded-lg">
                  <div className="text-muted-foreground grid grid-cols-[auto_1fr] gap-3 my-3 text-[#45474a]">
                    <SvgUser3 />
                    <p>{activeContact.contact_name}</p>

                    {!checkoutSuccessPopup?.state?.service.is_group_service ? (
                      meetingDetails ? (
                        <>
                          <SvgCalendar />
                          <p>
                            {isRecurringService && repeatBy === "dayOfWeek"
                              ? `${moment(firstMeetingDate).format(
                                  "MMMM D, YYYY"
                                )} @ ${formatRecurringSchedule()}`
                              : `${moment(meetingDetails.startTime).format(
                                  "h:mm A"
                                )} - ${moment(meetingDetails.endTime).format(
                                  "h:mm A, dddd"
                                )}, ${moment(meetingDetails.startTime).format(
                                  "MMMM D, YYYY"
                                )}`}
                          </p>
                          <SvgWebsite />
                          <p>{userTimezone}</p>
                          <SvgVideoOutline />
                          <p className="capitalize">
                            Web conferencing <br className="md:hidden" />{" "}
                            details to follow.
                          </p>
                        </>
                      ) : (
                        isGroupService &&
                        firstValidGroup &&
                        groupEnd && (
                          <>
                            <SvgCalendar />
                            <p>
                              {isRecurringService && repeatBy === "dayOfWeek"
                                ? `${moment(firstMeetingDate).format(
                                    "MMMM D, YYYY"
                                  )} @ ${formatRecurringSchedule()}`
                                : `${moment(firstValidGroup).format(
                                    "h:mm A"
                                  )} - ${moment(groupEnd).format(
                                    "h:mm A, dddd"
                                  )}, ${moment(firstValidGroup).format(
                                    "MMMM D, YYYY"
                                  )}`}
                            </p>
                            <SvgWebsite />
                            <p>{userTimezone}</p>
                            <SvgVideoOutline />
                            <p className="capitalize">
                              Web conferencing <br className="md:hidden" />{" "}
                              details to follow.
                            </p>
                          </>
                        )
                      )
                    ) : (
                      <>
                        <SvgUsersSolid />
                        <p>{service?.service_details?.title}</p>
                        <SvgFilledInformationCircle className="w-4 h-4" />
                        <p>
                          Please save the attached .ics file from the
                          confirmation email to add the event(s) to your
                          calendar.
                        </p>
                        <SvgWarning />
                        <p>
                          Group meetings have been scheduled. To cancel or opt
                          out, please contact your coach.
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <>
                <p className="text-2xl font-bold">
                  {service?.service_details?.type === "consultation"
                    ? "Continue to Scheduling"
                    : "Enrollment Confirmed"}
                </p>
              </>
            )}

            {meetingDetails || isGroupService ? (
              <>
                <Button onClick={handleStartMessage} size="lg">
                  Message {activeContact.contact_name}
                </Button>
              </>
            ) : (
              <div className="flex flex-col items-center text-center">
                <>
                  <p className="text-lg text-center mb-8">
                    {isFreeService ? (
                      <>
                        <p>
                          You{" "}
                          {(activeService || service)?.service_details?.type ===
                          "consultation"
                            ? "are confirmed for"
                            : "enrolled in"}{" "}
                          {(activeService || service)?.service_details?.title}.{" "}
                          {activeService?.availability_status === "inactive" ||
                          service?.availability_status === "inactive"
                            ? "Get started by messaging your coach."
                            : `Get started by scheduling your ${
                                (activeService || service)?.service_details
                                  ?.type === "consultation"
                                  ? "consultation."
                                  : "session."
                              }`}
                        </p>
                      </>
                    ) : (
                      <>
                        Thank you — payment completed via{" "}
                        {getPaymentSourceLabel()}.
                        <br />
                        You’re now enrolled in{" "}
                        <span className="font-semibold">
                          {(activeService || service)?.service_details?.title}
                        </span>
                        .<br />
                        <br />
                        {activeService?.availability_status === "inactive" ||
                        service?.availability_status === "inactive"
                          ? "Get started by messaging your coach."
                          : `Get started by scheduling your ${
                              (activeService || service)?.service_details
                                ?.type === "consultation"
                                ? "consultation."
                                : "session."
                            }`}
                      </>
                    )}
                  </p>

                  {activeService?.availability_status === "inactive" ||
                  service?.availability_status === "inactive" ? (
                    <Button size="lg" onClick={handleStartMessage}>
                      Message {activeContact?.contact_name}
                    </Button>
                  ) : (
                    <Button size="lg" onClick={handleScheduleSessionClick}>
                      {(activeService || service)?.service_details?.type ===
                      "consultation"
                        ? "Schedule Now"
                        : "Schedule Service"}
                    </Button>
                  )}
                </>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default CheckoutSuccessPopup
